import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Platform } from 'react-native';
import { Box, Text, VStack } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import { useOrbReducedMotion } from '@/common/components/presence/useOrbReducedMotion';
import type { OralExamLoungeHint } from '@/student/module/utils/oralExamLounge';

/** Fade-in, reading hold, then fade-out before the next tip replaces it. */
export const WAITING_HINT_FADE_IN_MS = 800;
export const WAITING_HINT_FADE_OUT_MS = 800;
export const WAITING_HINT_HOLD_MS = 8500;
export const WAITING_HINT_CYCLE_MS = (
  WAITING_HINT_FADE_IN_MS + WAITING_HINT_HOLD_MS + WAITING_HINT_FADE_OUT_MS
);

function HintCard({ title, body, index }: { title: string; body: string; index: number }) {
  return (
    <Box
      width="100%"
      testID={`oral-exam-waiting-hint-${index}`}
      accessibilityRole="text"
    >
      <Text size="md" fontWeight="semibold" color="$textBody" mb="$1" textAlign="center">
        {title}
      </Text>
      <Text size="sm" color="$textSubtle" textAlign="center">{body}</Text>
    </Box>
  );
}

/**
 * Game-loading tips: a static Tip eyebrow frames the slot. One title+body
 * fades in, holds, fades out, then the next replaces it. A single tenant hint
 * stays put after fade-in.
 */
export function WaitingHintCarousel({ hints }: { hints: OralExamLoungeHint[] }) {
  const { t } = useTranslation();
  const reducedMotion = useOrbReducedMotion();
  const [index, setIndex] = useState(0);
  const opacity = useRef(new Animated.Value(1)).current;
  const count = hints.length;
  const deckKey = hints.map((hint) => hint.title).join('\0');
  const hint = count === 0 ? undefined : hints[index % count];

  useEffect(() => {
    setIndex(0);
  }, [deckKey]);

  useEffect(() => {
    if (reducedMotion) {
      opacity.setValue(1);
      if (count <= 1) return undefined;
      const nextTimer = setTimeout(() => {
        setIndex((current) => (current + 1) % count);
      }, WAITING_HINT_HOLD_MS);
      return () => clearTimeout(nextTimer);
    }

    opacity.setValue(0);
    const fadeIn = Animated.timing(opacity, {
      toValue: 1,
      duration: WAITING_HINT_FADE_IN_MS,
      easing: Easing.out(Easing.ease),
      useNativeDriver: Platform.OS !== 'web',
    });
    fadeIn.start();

    if (count <= 1) {
      return () => {
        fadeIn.stop?.();
      };
    }

    const fadeOutTimer = setTimeout(() => {
      Animated.timing(opacity, {
        toValue: 0,
        duration: WAITING_HINT_FADE_OUT_MS,
        easing: Easing.in(Easing.ease),
        useNativeDriver: Platform.OS !== 'web',
      }).start();
    }, WAITING_HINT_FADE_IN_MS + WAITING_HINT_HOLD_MS);

    const nextTimer = setTimeout(() => {
      setIndex((current) => (current + 1) % count);
    }, WAITING_HINT_CYCLE_MS);

    return () => {
      fadeIn.stop?.();
      clearTimeout(fadeOutTimer);
      clearTimeout(nextTimer);
    };
  }, [index, opacity, reducedMotion, count, deckKey]);

  if (!hint) return null;

  return (
    <VStack
      space="sm"
      alignItems="center"
      width="100%"
      maxWidth={480}
      testID="oral-exam-waiting-hints"
      accessibilityLiveRegion="polite"
    >
      <Text
        size="xs"
        fontWeight="bold"
        color="$textHint"
        textTransform="uppercase"
        letterSpacing="$md"
        textAlign="center"
        testID="oral-exam-waiting-hint-eyebrow"
      >
        {t('oralExam.lobby.waiting.hintEyebrow')}
      </Text>
      <Animated.View
        style={{
          opacity,
          width: '100%',
          minHeight: 96,
        }}
      >
        <HintCard title={hint.title} body={hint.body} index={index} />
      </Animated.View>
    </VStack>
  );
}
